/**
 * Pure row helpers for the Plan Reports page (STRY0038480).
 *
 * Kept out of the component so the label, eligibility and filter rules are
 * unit-testable without rendering. The Reports page is the only consumer;
 * ExportModal is deliberately untouched by this story.
 */

const EM_DASH = '—';

// The report types start_export_job can record. 'full' is what every run on
// this branch records; 'summary' and 'full_all_releases' arrive once PR #607
// (STRY0038474) lands and its wizard starts sending exportMode.
export const REPORT_TYPE_LABELS = {
  summary: 'Summary',
  full: 'Full export',
  full_all_releases: 'Full export (all releases)'
};

/**
 * Label for a job's reportType. Historical rows predate the column and
 * legitimately have none — show a dash, never a broken value. An unrecognised
 * non-empty value is passed through so a mode added later is not swallowed.
 */
export function reportTypeLabel(value) {
  if (!value) return EM_DASH;
  return REPORT_TYPE_LABELS[value] || value;
}

export function projectsById(projects) {
  return (projects || []).reduce((acc, p) => {
    if (p && p.id) acc[p.id] = p;
    return acc;
  }, {});
}

/**
 * How to render a row's project cell.
 *
 * `linkable` is false when the project can't be resolved — the project was
 * deleted after the export ran, which is exactly how a failed export gets
 * produced. Linking would send the user to a dead route, so the raw id is
 * shown as plain text instead.
 */
export function projectLabel(row, byId) {
  const id = row?.projectId;
  if (!id) return { name: EM_DASH, linkable: false };
  const project = (byId || {})[id];
  if (!project || !project.name) return { name: id, linkable: false };
  return { name: project.name, linkable: true };
}

/**
 * Whether a download should be offered. Same predicate ExportModal uses, so
 * the two surfaces agree: a report is downloadable only once it has finished
 * and did not fail.
 */
export function canDownload(row) {
  return Boolean(row?.finished) && row?.status !== 'FAILED';
}

/** The statuses actually written are RUNNING, COMPLETED and FAILED. */
export function statusPillClass(status) {
  if (status === 'COMPLETED') return 'bz-pill bz-pill--ok';
  if (status === 'RUNNING') return 'bz-pill bz-pill--info';
  if (status === 'FAILED') return 'bz-pill bz-pill--bad';
  return 'bz-pill';
}

/** AND-combined client-side filters. An empty value means "all". */
export function filterRows(rows, { projectId, status }) {
  return (rows || []).filter(r => {
    if (projectId && r.projectId !== projectId) return false;
    if (status && r.status !== status) return false;
    return true;
  });
}

/**
 * Filter options derived from the rows actually present, so no option can
 * select an empty result.
 */
export function filterOptions(rows, byId) {
  const list = rows || [];

  const seenProjects = new Map();
  list.forEach(r => {
    if (!r.projectId || seenProjects.has(r.projectId)) return;
    seenProjects.set(r.projectId, projectLabel(r, byId).name);
  });
  const projects = Array.from(seenProjects, ([value, label]) => ({ value, label })).sort((a, b) =>
    a.label.localeCompare(b.label)
  );

  const statuses = Array.from(new Set(list.map(r => r.status).filter(Boolean))).sort();

  return { projects, statuses };
}
